import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Marquee from "react-fast-marquee";

const STORAGE_KEY = "jglinks_age_verified_v1";

/**
 * Age gate — cinematic editorial entry inspired by premium creator-agency sites.
 * Full-screen overlay shown once per browser. Stores acceptance in localStorage.
 */
export const AgeGate = ({ children }) => {
  const [verified, setVerified] = useState(true); // optimistic to avoid flash
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        if (data?.accepted) {
          setVerified(true);
          setHydrated(true);
          return;
        }
      }
    } catch { /* fall through */ }
    setVerified(false);
    setHydrated(true);
  }, []);

  const accept = () => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ accepted: true, at: new Date().toISOString() })
    );
    setVerified(true);
  };

  const decline = () => {
    window.location.href = "https://www.google.com";
  };

  if (!hydrated) {
    return <div className="fixed inset-0 bg-black" />;
  }

  return (
    <>
      <AnimatePresence>
        {!verified && (
          <motion.div
            key="age-gate"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, scale: 1.05 }}
            transition={{ duration: 0.5, ease: [0.65, 0, 0.35, 1] }}
            className="fixed inset-0 z-[100] bg-black text-white overflow-hidden grain"
            data-testid="age-gate"
          >
            {/* Cinematic backdrop */}
            <div
              className="absolute inset-0 bg-cover bg-center opacity-40"
              style={{
                backgroundImage:
                  "radial-gradient(ellipse at 30% 20%, rgba(244,63,94,0.35), transparent 55%), radial-gradient(ellipse at 80% 90%, rgba(245,158,11,0.25), transparent 60%), linear-gradient(180deg, #0a0a0a 0%, #000000 100%)",
              }}
            />
            {/* Ambient breathing glows */}
            <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-rose-500/25 blur-[120px] animate-glow-breath" />
            <div className="absolute -bottom-40 -right-40 w-[520px] h-[520px] rounded-full bg-amber-500/20 blur-[120px] animate-glow-breath" style={{ animationDelay: "2s" }} />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent" />

            {/* Top marquee — premium statement strip */}
            <div className="absolute top-0 left-0 right-0 border-b border-white/[0.06] bg-black/40 backdrop-blur-md py-2.5 z-10">
              <Marquee speed={42} gradient={false}>
                {[
                  "JGLINKS",
                  "·",
                  "BEAUTY THAT CONVERTS",
                  "·",
                  "PREMIUM CREATOR AGENCY",
                  "·",
                  "+18 ONLY",
                  "·",
                  "EST. 2024",
                  "·",
                  "TOP 0.1% TALENT",
                  "·",
                ].map((t, i) => (
                  <span
                    key={i}
                    className="mx-3 text-[10px] tracking-[0.3em] text-white/45 font-medium uppercase"
                  >
                    {t}
                  </span>
                ))}
              </Marquee>
            </div>

            {/* Content */}
            <motion.div
              initial="hidden"
              animate="show"
              variants={{
                hidden: {},
                show: { transition: { staggerChildren: 0.13, delayChildren: 0.2 } },
              }}
              className="relative h-full flex flex-col items-center justify-center px-6 pt-10"
            >
              {/* Section number */}
              <motion.p
                variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0 } }}
                transition={{ duration: 0.6, ease: [0.65, 0, 0.35, 1] }}
                className="text-[10px] tracking-[0.45em] text-white/40 uppercase mb-5"
              >
                · 00 / Entrada · Premium Members Only
              </motion.p>

              {/* Logo + brand */}
              <motion.div
                variants={{ hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0 } }}
                transition={{ duration: 0.7 }}
                className="flex items-center gap-3 mb-7"
              >
                <img
                  src="/logo.png"
                  alt="JGlinks"
                  className="w-11 h-11 rounded-xl ring-1 ring-white/15"
                  onError={(e) => { e.target.style.display = "none"; }}
                />
                <span className="font-display text-2xl tracking-tight">JGlinks</span>
              </motion.div>

              {/* Hero headline */}
              <motion.h1
                variants={{ hidden: { opacity: 0, y: 22 }, show: { opacity: 1, y: 0 } }}
                transition={{ duration: 0.8, ease: [0.65, 0, 0.35, 1] }}
                className="font-display text-[44px] sm:text-6xl leading-[0.92] text-center font-black tracking-tighter max-w-md mb-2"
              >
                ¿Tienes
                <br />
                <span className="italic font-medium bg-gradient-to-r from-rose-300 via-rose-200 to-amber-200 bg-clip-text text-transparent">
                  18+ años
                </span>
                ?
              </motion.h1>

              {/* Animated rule line */}
              <motion.div
                variants={{ hidden: { opacity: 0 }, show: { opacity: 1 } }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="h-px w-32 bg-gradient-to-r from-transparent via-white/40 to-transparent my-5 animate-draw-in"
              />

              {/* Subtext */}
              <motion.p
                variants={{ hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0 } }}
                transition={{ duration: 0.6 }}
                className="text-sm text-white/55 text-center leading-relaxed max-w-sm mb-9 px-3"
              >
                Este sitio contiene material explícito para mayores de edad.
                <br />
                Confirma tu edad para entrar al universo JGlinks.
              </motion.p>

              {/* CTA primary — shimmer pill */}
              <motion.div
                variants={{ hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0 } }}
                transition={{ duration: 0.65 }}
                className="w-full max-w-sm"
              >
                <button
                  onClick={accept}
                  data-testid="age-gate-accept"
                  className="cta-shine relative w-full overflow-hidden py-4 rounded-full bg-gradient-to-r from-rose-500 via-rose-400 to-amber-400 text-black font-black uppercase tracking-[0.18em] text-[13px] shadow-[0_18px_60px_-15px_rgba(244,63,94,0.55)] hover:scale-[1.015] active:scale-[0.985] transition-transform"
                >
                  <span className="relative z-10">Sí, soy mayor de 18</span>
                  <span className="cta-shine-bar" />
                </button>

                <button
                  onClick={decline}
                  data-testid="age-gate-decline"
                  className="w-full mt-3 py-3 rounded-full border border-white/10 text-white/55 hover:text-white hover:border-white/25 text-xs uppercase tracking-[0.25em] transition"
                >
                  No, llévame fuera
                </button>
              </motion.div>

              {/* Fine print */}
              <motion.p
                variants={{ hidden: { opacity: 0 }, show: { opacity: 1 } }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="text-[10px] text-white/30 mt-7 max-w-xs text-center leading-relaxed tracking-wide"
              >
                Al continuar aceptas nuestros{" "}
                <span className="underline decoration-white/40 underline-offset-2">Términos</span>{" "}
                y reconoces que el contenido es solo para adultos.
              </motion.p>
            </motion.div>

            {/* Bottom credit / live badge */}
            <div className="absolute bottom-5 left-0 right-0 flex items-center justify-between px-6 text-[10px] uppercase tracking-[0.3em] text-white/35">
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse-dot" />
                <span>En vivo · 24/7</span>
              </div>
              <span>Made in Colombia</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {verified && children}
    </>
  );
};

export default AgeGate;
